import ErrorPopup from "./error-popup";
import HttpClient from "./http-client";
import { stringLocalizer } from "./main";
import MagnificPopup, { IPopup, PopupOptions } from "./popup";
import { flushServerAnalyticsEvents } from "./analytics";

export interface QueryParameter {
    key: string;
    value: string | number;
}

let devLoggingEnabled: boolean | null = null;

export function isDevLoggingEnabled(): boolean {
    if (devLoggingEnabled !== null) {
        return devLoggingEnabled;
    }

    const host = window.location.hostname;
    devLoggingEnabled = host === "localhost"
        || host.endsWith(".local")
        || host.includes(".dev.");
    return devLoggingEnabled;
}

export function enableDevLoggingFromSettings(isDevelopment: boolean): void {
    if (isDevelopment) {
        devLoggingEnabled = true;
    }
}

export function devLog(label: string, ...args: unknown[]): void {
    if (isDevLoggingEnabled()) {
        console.log(`[TronShop] ${label}`, ...args);
    }
}

let eventListeners = new Map<HTMLElement, string>();

export const isVisible = (element: HTMLElement): boolean => element && element.offsetWidth > 0 && element.offsetHeight > 0;

export const hide = (element: HTMLElement): void => { element.style.display = 'none' };

export const show = (element: HTMLElement): void => { element.style.display = 'block' };

export const addClass = (element: HTMLElement, className: string): void => { if (!element.classList.contains(className)) element.classList.add(className); }

export const removeClass = (element: HTMLElement, className: string): void => { if (element.classList.contains(className)) element.classList.remove(className); }

export const disable = (element: HTMLElement): void => { if (!element.hasAttribute('disabled')) element.setAttribute('disabled', 'disabled') }
export const enable = (element: HTMLElement): void => { if (element.hasAttribute('disabled')) element.removeAttribute('disabled') }

export const setReadonly = (element: HTMLElement): void => { if (!element.hasAttribute('readonly')) element.setAttribute('readonly', '') }
export const unsetReadonly = (element: HTMLElement): void => { if (element.hasAttribute('readonly')) element.removeAttribute('readonly') }

export function includes<T>(array: T[], item: T): boolean {
    return array.indexOf(item) >= 0;
}

export async function asyncForEach<T>(array: T[], callback: (item: T) => Promise<void>) {
    for (let index = 0; index < array.length; index++) {
        await callback(array[index]);
    }
}

export const toggleClass = (element: HTMLElement, className: string): void => {
    if (element.classList.contains(className)) {
        element.classList.remove(className);
    } else {
        element.classList.add(className);
    }
}

export function convertToHtmlElement(htmlString: string): HTMLElement {
    const div = document.createElement('div');
    div.innerHTML = htmlString.trim();
    return div.firstElementChild as HTMLElement;
}

export function debounce(func: () => void, delay: number, immediate: boolean) {
    let timeout;
    return function () {
        const context = this, args = arguments;
        const later = function () {
            timeout = null;
            if (!immediate) func.apply(context, args);
        };
        const callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, delay);
        if (callNow) func.apply(context, args);
    };
}

export function getBaseUrl(): string {
    return `${window.location.protocol}//${window.location.host}/`;
}

export function getApiUrl(): string {
    return `${getBaseUrl()}api`;
}

function removeGetParameters(url: string): string {
    return url.split('?')[0];
}

function removeAnchor(url: string): string {
    return url.split('#')[0];
}

export function getUrlWithoutQueryString(): string {
    let url = window.location.href;
    url = removeGetParameters(url);
    url = removeAnchor(url);
    return url;
}

export function getQueryString(): string {
    const urlSplit = window.location.href.split('?');
    return urlSplit.length === 2 ? urlSplit[1] : '';
}

export function getQueryParameters(): QueryParameter[] {
    const queryString = getQueryString();
    if (!queryString)
        return [];

    return queryString
        .split('&')
        .map(param => {
            const split = param.split('=');
            return { key: split[0], value: split[0] } as QueryParameter
        });
}

export function addLeadingSlash(url: string): string {
    if (!url || url[0] === '/') {
        return url;
    }
    return `/${url}`;
}

export function removeLeadingSlash(url: string): string {
    if (!url || url[0] !== '/') {
        return url;
    }
    return url.substring(1);
}

export function getCurrentLanguageId(): string {
    const languageAwareElement = document.querySelector('[data-current-language-id]') as HTMLElement;
    return languageAwareElement?.dataset.currentLanguageId;
}

function buildQueryParameter(queryParameter: QueryParameter): string {
    return `${queryParameter.key}=${queryParameter.value}`;
}

export function buildQueryParameters(queryParameters: QueryParameter[]): string {
    if (!queryParameters) {
        return '';
    }

    const queryParametersWithValue = queryParameters.filter(p => p.value);
    const concatenatedParametes = queryParametersWithValue.map(i => buildQueryParameter(i));
    const parameters = concatenatedParametes.join('&');

    return parameters;
}

export function getPopup(options: PopupOptions = null): IPopup {
    return new MagnificPopup(options);
}

export function displayError(message: string, title: string = null) {
    new ErrorPopup().display(message, title);
}

export async function openInPopup(url: string, options: PopupOptions): Promise<IPopup> {
    const popup = getPopup(options);
    const response = await new HttpClient().fetch(encodeURI(url));
    const content = await response.text();
    popup.open(content);
    flushPopupAnalyticsEvents();
    return popup;
}

export async function openInPopupWithPlaceholder(url: string, options: PopupOptions): Promise<IPopup> {
    const popup = getPopup(options);
    await popup.openAndLoadAsync(async () => {
        const response = await new HttpClient().fetch(encodeURI(url));
        return await response.text();
    });
    flushPopupAnalyticsEvents();
    return popup;
}

/** Popup HTML goes in through magnificPopup, not the ajax dispatcher: send its server analytics payloads (e.g. the cart dialog's similar products list). */
function flushPopupAnalyticsEvents(): void {
    const popupContent = document.querySelector('.mfp-content');
    if (popupContent) {
        flushServerAnalyticsEvents(popupContent);
    }
}

export async function openComponentInPopup(viewComponent: string, queryParams: QueryParameter[] = null, options: PopupOptions, usePlaceholder = false): Promise<IPopup> {
    if (!queryParams) {
        queryParams = [];
    }

    queryParams.push({ key: 'viewComponent', value: viewComponent });

    const url = `${getUrlWithoutQueryString()}?${buildQueryParameters(queryParams)}`;
    return usePlaceholder
        ? await openInPopupWithPlaceholder(url, options)
        : await openInPopup(url, options);
}

export function extractQueryStringFromCurrentUrl(): string {
    let queryParameters = '';
    const currentUrl = window.location.href.split('?');
    if (currentUrl.length > 1) {
        queryParameters = currentUrl[1];
    }

    return queryParameters;
}

export async function loadAsyncComponent(e: Element, optionalQueryParams: QueryParameter[] = null): Promise<HTMLElement> {
    const element = e as HTMLElement;
    const componentName = element.dataset.component;
    const id = element.dataset.componentId;

    let queryParameters = extractQueryStringFromCurrentUrl();
    if (optionalQueryParams != null && optionalQueryParams.length > 0) {
        const params = buildQueryParameters(optionalQueryParams);
        queryParameters = (queryParameters ? `${queryParameters}&${params}` : `${params}`);
    }

    const newQuery = (queryParameters ? `${queryParameters}&` : '') + `viewComponent=${componentName}&componentId=${id}&ajax=true`;

    const response = await new HttpClient().fetch(`${getUrlWithoutQueryString()}?${newQuery}`);
    const content = await response.text();
    const resultElement = convertToHtmlElement(content);

    if (resultElement) {
        element.replaceWith(resultElement);
        flushServerAnalyticsEvents(resultElement);
        return resultElement;
    }

    element.innerHTML = content;
    flushServerAnalyticsEvents(element);
    return element;
}

/// Loads several deferred components with ONE request: viewComponent=Name_id;Name2&ajax=true.
/// The server-side ComponentsComposer renders all of them in one pass, so N placeholders cost a
/// single HTTP round-trip (and a single page-context resolution) instead of N.
/// Returns a map placeholder element -> rendered element for the components found in the response.
export async function loadAsyncComponents(elements: HTMLElement[], optionalQueryParams: QueryParameter[] = null): Promise<Map<HTMLElement, HTMLElement>> {
    const replaced = new Map<HTMLElement, HTMLElement>();
    if (!elements || elements.length === 0) {
        return replaced;
    }

    const componentParam = elements
        .map(e => e.dataset.componentId ? `${e.dataset.component}_${e.dataset.componentId}` : e.dataset.component)
        .join(';');

    // The inline bootstrap in _Layout may have already fired this exact request while the bundle
    // was still downloading — consume it instead of fetching again. Single-shot: the prefetch is
    // cleared on first use so later calls (e.g. variant switching) fetch normally.
    let content: string = null;
    const prefetch = (window as any).__deferredComponentsPrefetch;
    if (prefetch && prefetch.param === componentParam) {
        (window as any).__deferredComponentsPrefetch = null;
        content = await prefetch.text;
    }

    if (content == null) {
        let queryParameters = extractQueryStringFromCurrentUrl();
        if (optionalQueryParams && optionalQueryParams.length > 0) {
            const params = buildQueryParameters(optionalQueryParams);
            queryParameters = (queryParameters ? `${queryParameters}&${params}` : params);
        }
        const newQuery = (queryParameters ? `${queryParameters}&` : '') + `viewComponent=${componentParam}&ajax=true`;
        const response = await new HttpClient().fetch(`${getUrlWithoutQueryString()}?${newQuery}`);
        content = await response.text();
    }

    const serverDom = new DOMParser().parseFromString(content, 'text/html');

    elements.forEach(element => {
        const rendered = serverDom.querySelector(`[data-component="${element.dataset.component}"]`) as HTMLElement;
        if (rendered) {
            element.replaceWith(rendered);
            replaced.set(element, rendered);
        }
    });

    flushServerAnalyticsEvents();
    return replaced;
}

export function localize(name: string): string {
    return stringLocalizer.get(name);
}

export function getComponentPart(element: HTMLElement, componentPartName: string): HTMLElement {
    return element.querySelector(`[data-component-part="${componentPartName}"]`) as HTMLElement;
}
export function getComponentParts(element: HTMLElement, componentPartName: string): HTMLElement[] {
    return [...element.querySelectorAll(`[data-component-part="${componentPartName}"]`)]
        .map(e => e as HTMLElement);
}
export function getComponent(element: HTMLElement, componentName: string) {
    return element.querySelector(`[data-component="${componentName}"]`) as HTMLElement;
}

export function getComponentById(element: HTMLElement, componentId: string) {
    return element.querySelector(`[data-component-id="${componentId}"]`) as HTMLElement;
}

export function addEventListenerIfNotExists(element: HTMLElement, event: string, listener: (this: HTMLElement, ev: Event) => any) {
    if (!eventListeners.has(element) || eventListeners.get(element) != event) {
        element.addEventListener(event, listener);
        eventListeners.set(element, event);
    }
}

export function getAllowedCookies() {
    const allowed = getCookie('AllowedCookies');
    return allowed.split('|');
}

export function getCookie(cookieName) {
    const name = cookieName + '=';
    const decodedCookies = decodeURIComponent(document.cookie);
    const cookiesArray = decodedCookies.split(';');

    for (let i = 0; i < cookiesArray.length; i++) {
        let cookie = cookiesArray[i];
        while (cookie.charAt(0) === ' ') {
            cookie = cookie.substring(1);
        }
        if (cookie.indexOf(name) === 0) {
            return cookie.substring(name.length, cookie.length);
        }
    }
    return '';
}
